import type { ProjectDefinition } from "../../../types";
import { type FuncionProjectSpec, NOMBRES, conditions, seededRandom } from "../spec";

const SHEET = "TX Correos";
const TABLE = "Contactos";

const DOMINIOS = ["empresa.com.mx", "grupoaltamira.mx", "correo.com", "distribuidoraluna.com.mx"] as const;
const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio"] as const;
const REPORTES = ["ventas_norte", "inventario_general", "cobranza", "nomina_quincenal", "gastos_viaje"] as const;
const EXTENSIONES = ["xlsx", "csv", "pdf"] as const;
const MARCAS = ["DELL", "HP", "LENOVO", "ASUS", "ACER", "APPLE"] as const;
const TIPOS = ["LAP", "MON", "IMP", "TAB"] as const;

const rnd = seededRandom(3131);
const sinAcentos = (s: string) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

// Contacto (Apellidos, Nombre), Correo, Ruta, SKU
const ROWS = NOMBRES.slice(0, 30).map((nombre) => {
  const [first, last] = nombre.split(" ");
  const segundo = rnd.pick(["Herrera", "Morales", "Ríos", "Vega", "Cruz", "Luna"]);
  const user = `${sinAcentos(first)}.${sinAcentos(last)}${rnd.next() < 0.3 ? rnd.int(1, 99) : ""}`;
  const ruta = `C:\\Reportes\\2026\\${rnd.pick(MESES)}\\${rnd.pick(REPORTES)}.${rnd.pick(EXTENSIONES)}`;
  const sku = `${rnd.pick(TIPOS)}-${rnd.pick(MARCAS)}-${rnd.pick(["14", "15", "16", "24", "27"])}-${rnd.pick(["I5", "I7", "R5", "M2"])}`;
  return [`${last} ${segundo}, ${first}`, `${user}@${rnd.pick(DOMINIOS)}`, ruta, sku] as [string, string, string, string];
});

type Row = (typeof ROWS)[number];
const antes = (s: string, d: string) => s.slice(0, s.indexOf(d));
const despues = (s: string, d: string) => s.slice(s.indexOf(d) + d.length);
const despuesUltimo = (s: string, d: string) => s.slice(s.lastIndexOf(d) + d.length);
const partesRuta = (r: Row) => r[2].split("\\");

export const spec: FuncionProjectSpec = {
  projectId: "funciones-antes-despues",
  sheets: [
    {
      name: SHEET,
      title: "Correos, rutas y SKUs",
      subtitle: "Textos con partes de largo variable — sepáralos por sus delimitadores",
      tables: [
        {
          name: TABLE,
          anchor: "A4",
          headers: ["Contacto", "Correo", "Ruta", "SKU"],
          rows: ROWS,
          answerColumns: ["Usuario", "Dominio", "Nombre", "Marca", "Archivo", "Extensión", "Mes", "Nombre completo"],
        },
      ],
    },
  ],
  checks: {
    1: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Usuario", fns: ["TEXTBEFORE"], exact: true,
      expected: ROWS.map((r: Row) => antes(r[1], "@")),
    },
    2: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Dominio", fns: ["TEXTAFTER"], exact: true,
      expected: ROWS.map((r) => despues(r[1], "@")),
    },
    3: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Nombre", fns: ["TEXTAFTER"], exact: true,
      expected: ROWS.map((r) => despues(r[0], ", ")),
    },
    4: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Marca", fns: ["TEXTBEFORE", "TEXTAFTER"], exact: true,
      expected: ROWS.map((r) => r[3].split("-")[1]),
    },
    5: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Archivo", fns: ["TEXTAFTER"], exact: true,
      expected: ROWS.map((r) => despuesUltimo(r[2], "\\")),
    },
    6: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Extensión", fns: ["TEXTAFTER"], exact: true,
      expected: ROWS.map((r) => despuesUltimo(r[2], ".")),
    },
    7: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Mes", fns: ["TEXTBEFORE", "TEXTAFTER"], exact: true,
      expected: ROWS.map((r) => partesRuta(r)[partesRuta(r).length - 2]),
    },
    8: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Nombre completo", fns: ["TEXTBEFORE", "TEXTAFTER"],
      exact: true,
      expected: ROWS.map((r) => `${despues(r[0], ", ")} ${antes(r[0], ",")}`),
    },
  },
};

const F = (text: string) => `<span class="formula">${text}</span>`;

export const project: ProjectDefinition = {
  id: "funciones-antes-despues",
  title: "TEXTOANTES y TEXTODESPUES: Correos, rutas y SKUs",
  subtitle: "Cortar texto por un delimitador, no por posición",
  description:
    "Los correos, las rutas de archivo y los SKU no tienen largo fijo: \"ana.lopez@empresa.com.mx\" y \"luis@correo.com\" tienen la @ en posiciones distintas. Con IZQUIERDA y EXTRAE tendrías que calcular posiciones con HALLAR.\n\nTEXTOANTES y TEXTODESPUES cortan el texto justo antes o después de un delimitador. Con el argumento de instancia puedes elegir la 2.ª aparición, o contar desde el final con números negativos.",
  topics: [
    "Antes y después de un delimitador",
    "Delimitadores de más de un carácter (\", \")",
    "Instancias: la 2.ª aparición del guion",
    "Instancias negativas: contar desde el final",
    "Reacomodar \"Apellidos, Nombre\"",
  ],
  tasks: [
    {
      id: 1,
      level: "basico",
      title: "Tarea 1: Usuario del correo",
      description: "En la hoja <strong>TX Correos</strong>, columna <strong>Usuario</strong>: todo lo que está <strong>antes de la @</strong> del Correo.",
      solution: [`Selecciona ${F("E5")}.`, `Escribe ${F('=TEXTOANTES([@Correo],"@")')}.`],
      expectedResult: "ana.lopez",
      finalFormula: '=TEXTOANTES([@Correo],"@")',
    },
    {
      id: 2,
      level: "basico",
      title: "Tarea 2: Dominio del correo",
      description: "En la columna <strong>Dominio</strong>: todo lo que está <strong>después de la @</strong>.",
      solution: [`Selecciona ${F("F5")}.`, `Escribe ${F('=TEXTODESPUES([@Correo],"@")')}.`],
      expectedResult: "empresa.com.mx",
      finalFormula: '=TEXTODESPUES([@Correo],"@")',
    },
    {
      id: 3,
      level: "basico",
      title: "Tarea 3: Nombre de pila",
      description:
        "En la columna <strong>Nombre</strong>: el Contacto viene como \"López Herrera, Ana\". Obtén lo que está después de la <strong>coma y el espacio</strong>.",
      solution: [
        `Selecciona ${F("G5")}.`,
        `El delimitador puede tener varios caracteres: ${F('", "')} (coma y espacio) evita que el nombre empiece con un espacio.`,
        `Escribe ${F('=TEXTODESPUES([@Contacto],", ")')}.`,
      ],
      expectedResult: "Ana",
      finalFormula: '=TEXTODESPUES([@Contacto],", ")',
    },
    {
      id: 4,
      level: "intermedio",
      title: "Tarea 4: Marca del SKU",
      description:
        "En la columna <strong>Marca</strong>: el SKU tiene la forma TIPO-MARCA-TAMAÑO-PROCESADOR (LAP-DELL-15-I7). Obtén la <strong>marca</strong>, que es lo que está entre el <strong>1.er y el 2.º guion</strong>.",
      solution: [
        `Selecciona ${F("H5")}.`,
        `Primero quita lo que está antes del primer guion: ${F('TEXTODESPUES([@SKU],"-")')} da \"DELL-15-I7\". Luego quédate con lo que está antes del siguiente guion.`,
        `Escribe ${F('=TEXTOANTES(TEXTODESPUES([@SKU],"-"),"-")')}.`,
      ],
      expectedResult: "DELL",
      finalFormula: '=TEXTOANTES(TEXTODESPUES([@SKU],"-"),"-")',
    },
    {
      id: 5,
      level: "intermedio",
      title: "Tarea 5: Nombre del archivo",
      description:
        "En la columna <strong>Archivo</strong>: de la Ruta (C:\\Reportes\\2026\\Marzo\\ventas_norte.xlsx) obtén solo el nombre del archivo, que es lo que está después de la <strong>última</strong> diagonal invertida \\.",
      solution: [
        `Selecciona ${F("I5")}.`,
        `El tercer argumento es la instancia. Con ${F("-1")} cuenta desde el final: la última \\.`,
        `Escribe ${F('=TEXTODESPUES([@Ruta],"\\",-1)')}.`,
      ],
      expectedResult: "ventas_norte.xlsx",
      finalFormula: '=TEXTODESPUES([@Ruta],"\\",-1)',
    },
    {
      id: 6,
      level: "intermedio",
      title: "Tarea 6: Extensión",
      description: "En la columna <strong>Extensión</strong>: lo que está después del <strong>último punto</strong> de la Ruta (xlsx, csv o pdf).",
      solution: [`Selecciona ${F("J5")}.`, `Escribe ${F('=TEXTODESPUES([@Ruta],".",-1)')}.`],
      expectedResult: "xlsx",
      finalFormula: '=TEXTODESPUES([@Ruta],".",-1)',
    },
    {
      id: 7,
      level: "reto",
      title: "Tarea 7: Carpeta del mes",
      description: conditions(
        "En la columna <strong>Mes</strong>: obtén el nombre de la carpeta donde está el archivo (Marzo). Es lo que está <strong>entre las dos últimas \\</strong>:",
        [
          "Corta después de la <strong>penúltima</strong> \\ (instancia -2) → \"Marzo\\ventas_norte.xlsx\"",
          "De eso, quédate con lo que está <strong>antes</strong> de la \\",
        ]
      ),
      solution: [
        `Selecciona ${F("K5")}.`,
        `Escribe ${F('=TEXTOANTES(TEXTODESPUES([@Ruta],"\\",-2),"\\")')}.`,
      ],
      expectedResult: "Marzo",
      finalFormula: '=TEXTOANTES(TEXTODESPUES([@Ruta],"\\",-2),"\\")',
    },
    {
      id: 8,
      level: "reto",
      title: "Tarea 8: Nombre en orden natural",
      description:
        "En la columna <strong>Nombre completo</strong>: reacomoda \"López Herrera, Ana\" como <strong>Ana López Herrera</strong> (nombre, espacio, apellidos).",
      solution: [
        `Selecciona ${F("L5")}.`,
        "La parte después de \", \" es el nombre y la parte antes de la coma son los apellidos. Únelos con un espacio.",
        `Escribe ${F('=TEXTODESPUES([@Contacto],", ")&" "&TEXTOANTES([@Contacto],",")')}.`,
      ],
      expectedResult: "Ana López Herrera",
      finalFormula: '=TEXTODESPUES([@Contacto],", ")&" "&TEXTOANTES([@Contacto],",")',
    },
  ],
};
